import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MotionConfig } from 'motion/react';
import { BrowserRouter } from 'react-router';
import { LocaleProvider, ThemeProvider, ToastProvider } from 'escrowbuild-ui';

import { App } from './App';
import { CATALOGUES } from './i18n';
import { SessionProvider } from './lib/session';
import './styles/index.css';

const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');

createRoot(container).render(
  <StrictMode>
    {/*
      The same three providers the design system's own stories run inside, in the
      same nesting order — components are written against this environment and
      several of them throw without their provider rather than degrading.

      `LocaleProvider` sits outside all of them and holds the interface language.
      It is outermost because the language is not a property of any of them: the
      theme, the motion policy, the toast host, the session and the router all
      render WORDS, and every one of them has to be inside the catalogue that
      decides which words. Its own position above `BrowserRouter` is deliberate
      too — the active language has nothing to do with the URL, and the sign-in
      screen renders outside every route.

      `ThemeProvider` is what sets `data-theme` on <html> and notifies every
      component that reads the resolved theme (the brand mark, the theme
      switcher, the chart palette). The inline script in `index.html` has already
      applied the stored choice before first paint; the provider adopts that
      state rather than re-deriving it, so there is no second flash.

      `MotionConfig reducedMotion="user"` is set once here so no component has to
      remember it. It is the reason individual components can use `initial`
      freely: under `prefers-reduced-motion` Motion skips the transition, and the
      components that need the stronger guarantee (no `initial` at all on the
      first painted frame) handle that themselves.

      `ToastProvider` wraps the router because a toast is raised from a page
      action and must outlive the page that raised it.

      `SessionProvider` wraps the router too, and for the same reason turned up:
      the callback route is outside both shells and the shell reads the session
      to decide whether to render at all, so the session has to belong to the
      app rather than to either shell. It sits above `BrowserRouter` because it
      reads `sessionStorage` rather than the URL, and nothing it does needs a
      route.
    */}
    <LocaleProvider catalogue={CATALOGUES}>
      <ThemeProvider>
        <MotionConfig reducedMotion="user">
          <ToastProvider position="bottom-right">
            <SessionProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </SessionProvider>
          </ToastProvider>
        </MotionConfig>
      </ThemeProvider>
    </LocaleProvider>
  </StrictMode>,
);
